import { z } from "@/zod";
import { t } from "i18next";
import { formatCommaDecimalToDot } from "@/lib/utils/formatters";

/**
 * Numbers persisted and sent to the API use `.` as decimal separator; the UI shows `,` via
 * `formatNumber`. Form strings are normalized with `formatCommaDecimalToDot` before parse/validation.
 */

export const NUMBER_SCHEMA = "NumberSchema";

/**
 * After `formatCommaDecimalToDot`, value must be entirely a non-negative decimal.
 * (`parseFloat` alone is not enough — it ignores trailing junk, e.g. `"12.3x"` → 12.3.)
 */
const NON_NEGATIVE_DECIMAL_DOT_STRING = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

function isStrictNonNegativeDecimalString(normalized: string): boolean {
  if (normalized === "") return false;
  return NON_NEGATIVE_DECIMAL_DOT_STRING.test(normalized);
}

function fractionalDigitsCount(normalized: string): number {
  const dot = normalized.indexOf(".");
  if (dot === -1) return 0;
  return normalized.length - dot - 1;
}

/**
 * Nullable string coerced from form values, parsed as a number, must be ≤ `max`.
 * Pairs with `applyEmptyStringsToNulls`: empty input becomes `null` before this runs.
 */
export function createNullableBoundedNumericStringSchema({
  max,
  message,
}: {
  max: number;
  message?: string;
}) {
  const resolvedMessage =
    message ?? t("validation.bounded_numeric.range", { max });

  return z.union([
    z.null(),
    z.coerce
      .string()
      .refine(
        (val) => {
          const normalized = formatCommaDecimalToDot(String(val).trim());
          if (!isStrictNonNegativeDecimalString(normalized)) return false;
          const n = parseFloat(normalized);
          return !isNaN(n) && isFinite(n);
        },
        { message: t("validation.invalid_numeric") },
      )
      .refine(
        (val) => {
          const normalized = formatCommaDecimalToDot(String(val).trim());
          const n = parseFloat(normalized);
          return n <= max;
        },
        { message: resolvedMessage },
      )
      .transform((val) => formatCommaDecimalToDot(String(val).trim())),
  ]);
}

export const createNonNegativeDecimalStringSchema = (decimalPoints = 2) => {
  return z
    .string()
    .refine(
      (val) => {
        const normalized = formatCommaDecimalToDot(val.trim());
        if (!isStrictNonNegativeDecimalString(normalized)) return false;
        const value = parseFloat(normalized);
        return !isNaN(value) && value >= 0;
      },
      { message: t("validation.invalid_numeric") },
    )
    .refine(
      (val) => {
        const normalized = formatCommaDecimalToDot(val.trim());
        return fractionalDigitsCount(normalized) <= decimalPoints;
      },
      {
        message: t("validation.decimal_places_max", { max: decimalPoints }),
      },
    )
    .transform((val) =>
      parseFloat(formatCommaDecimalToDot(val.trim())).toFixed(decimalPoints),
    )
    .describe(NUMBER_SCHEMA);
};

export const nonNegativeDecimalStringSchema =
  createNonNegativeDecimalStringSchema();

// Default max matches cloud payroll-types percentage column
export const createNonNegativeDecimalPercentageSchema = (
  max = 999.99,
  decimalPoints = 2,
) =>
  createNonNegativeDecimalStringSchema(decimalPoints).refine(
    (value) => parseFloat(value) <= max,
    {
      message: t("validation.bounded_numeric.range", { max }),
    },
  );

/**
 * Non-negative integer only: digits only, no decimal separator in the normalized (dot) form.
 * Use with `NumberInput` + `z.coerce.string()` when the API expects an integer.
 */
export const nonNegativeIntegerStringSchema = z
  .string()
  .refine(
    (val) => {
      const normalized = formatCommaDecimalToDot(String(val).trim());
      if (normalized === "") return false;
      return /^\d+$/.test(normalized);
    },
    { message: t("validation.integer_only") },
  )
  .transform((val) =>
    parseInt(formatCommaDecimalToDot(String(val).trim()), 10),
  );
